// Live, client-side password strength meter for the Devise auth forms.
//
// Advisory UX only — the real gate is the server-side PasswordRules check on
// the User model. This meter reads the same thresholds (min length, min score)
// from data attributes on the password input so the feedback never drifts from
// what the server will actually accept.
//
// Enable by rendering the password input with:
//   data: { password_strength: true, min_length: N, min_score: N }
// The meter DOM is built by this script and inserted after the input, so no
// extra markup is needed in the view.
import zxcvbn from 'zxcvbn';
// zxcvbn score is 0 (weakest) .. 4 (strongest); index by score.
const SCORE_CLASSES = [
    'bg-danger',
    'bg-danger',
    'bg-warning',
    'bg-info',
    'bg-success'
];
const SCORE_LABELS = [
    'Very weak',
    'Weak',
    'Fair',
    'Good',
    'Strong'
];
// Other field values (email, name) that zxcvbn should penalise the password for
// resembling — mirrors the user_inputs passed to the gem on the server.
function collectUserInputs(input) {
    const form = input.closest('form');
    if (!form) return [];
    const candidates = form.querySelectorAll('input[type="email"], input[type="text"]');
    return Array.from(candidates).filter((el)=>el !== input && el.value).map((el)=>el.value);
}
function buildMeter(input) {
    // Rebuild on every turbolinks:load so listeners are always live.
    const existing = input.nextElementSibling;
    if (existing && existing.classList.contains('password-strength')) existing.remove();
    const wrapper = document.createElement('div');
    wrapper.className = 'password-strength mt-1';
    wrapper.setAttribute('aria-live', 'polite');
    const progress = document.createElement('div');
    progress.className = 'progress';
    progress.style.height = '4px';
    const bar = document.createElement('div');
    bar.className = 'progress-bar';
    bar.setAttribute('role', 'progressbar');
    bar.style.width = '0%';
    progress.appendChild(bar);
    const feedback = document.createElement('small');
    feedback.className = 'password-strength__feedback form-text text-muted';
    wrapper.appendChild(progress);
    wrapper.appendChild(feedback);
    input.insertAdjacentElement('afterend', wrapper);
    return {
        bar,
        feedback
    };
}
function initPasswordStrengthMeters() {
    document.querySelectorAll('input[data-password-strength]').forEach((input)=>{
        const minLength = parseInt(input.dataset.minLength, 10) || 0;
        const minScore = parseInt(input.dataset.minScore, 10) || 0;
        // Tear down any listener bound on a previous turbolinks:load (the same
        // input element can be reused from the Turbolinks cache) before binding a
        // fresh one, so stale closures don't accumulate.
        if (input.strengthAbortController) input.strengthAbortController.abort();
        const controller = new AbortController();
        input.strengthAbortController = controller;
        const { bar, feedback } = buildMeter(input);
        const update = ()=>{
            const value = input.value || '';
            if (!value) {
                bar.style.width = '0%';
                bar.className = 'progress-bar';
                feedback.textContent = '';
                feedback.classList.add('text-muted');
                feedback.classList.remove('text-success');
                return;
            }
            const result = zxcvbn(value, collectUserInputs(input));
            const { score } = result;
            bar.style.width = `${(score + 1) / SCORE_LABELS.length * 100}%`;
            bar.className = `progress-bar ${SCORE_CLASSES[score]}`;
            const meetsLength = value.length >= minLength;
            const meetsScore = score >= minScore;
            let message = SCORE_LABELS[score];
            if (!meetsLength) {
                message += ` — needs at least ${minLength} characters`;
            } else if (!meetsScore) {
                const warning = result.feedback && result.feedback.warning;
                message += warning ? ` — ${warning}` : ' — too easy to guess';
            }
            feedback.textContent = message;
            const acceptable = meetsLength && meetsScore;
            feedback.classList.toggle('text-success', acceptable);
            feedback.classList.toggle('text-muted', !acceptable);
        };
        input.addEventListener('input', update, {
            signal: controller.signal
        });
        update();
    });
}
window.addEventListener('turbolinks:load', initPasswordStrengthMeters);
